/**
 * Bridge between WOD: PreOrder Now and Horizon's buy buttons.
 *
 * WOD has Horizon support, but this shop's WOD config is set to a generic
 * theme ("Default"), so its generic code paths run. On a variant change WOD
 * reacts before Horizon has re-rendered the buy buttons, then Horizon's morph
 * wipes WOD's work. Left alone that means:
 *   - the pre-order label is lost (button reverts to "Add to cart");
 *   - WOD's `.text()` reset strips Horizon's cart icon from the button;
 *   - WOD appends `selling_plan=undefined`, which /cart/add rejects (400),
 *     so nothing can be added to the cart after changing colour.
 *
 * This file re-applies WOD's own settings after Horizon settles, and guards
 * the cart request itself. It reads everything from WOD's `window.pn` data,
 * so button text / note wording stay managed in the WOD app. Remove it once
 * WOD runs with its Horizon config.
 */

const FORM_SELECTOR = 'product-form-component form[data-type="add-to-cart-form"]';
const LABEL_TEMPLATE_ID = 'PreorderBridgeButtonLabel';
const NUMERIC = /^\d+$/;

/** WOD's per-variant settings, keyed by variant id. Read lazily: WOD loads async. */
function wodSettingFor(variantId) {
  const settings = window.pn?.settings?.singleProductSettings;
  if (!settings || !variantId) return null;
  for (const setting of Object.values(settings)) {
    if (setting && String(setting.n) === String(variantId)) return setting;
  }
  return null;
}

/** Pre-order is on for this variant in WOD (`b` is WOD's enabled flag, `a` its button text). */
function isPreorderVariant(variantId) {
  const setting = wodSettingFor(variantId);
  return !!setting && String(setting.b) === 'true';
}

function noteField() {
  const shop = window.pn?.shop ?? {};
  return {
    name: `properties[${shop.pn_note_label || 'Note'}]`,
    value: shop.cart_label_text || 'Pre-order item',
  };
}

/* ------------------------------------------------------------------ *
 * 1. Buy buttons: re-apply WOD's label and note after Horizon renders
 * ------------------------------------------------------------------ */

/**
 * Read/write access to the button's label, across Horizon versions:
 *   - up to 4.1.1 the text sits in `.add-to-cart-text__content > span > span`;
 *   - 4.1.5 renders the icon, a bare text node, then the checkmark span.
 * WOD's generic reset replaces the button's whole content with plain text,
 * dropping the icon and checkmark. In that case rebuild the stock markup from
 * the template in theme.liquid (which matches this theme's version).
 * @param {HTMLButtonElement} button
 * @param {boolean} [rebuilt]
 * @returns {{get: () => string, set: (text: string) => void} | null}
 */
function buttonLabel(button, rebuilt = false) {
  const nested = button.querySelector('.add-to-cart-text__content > span > span');
  if (nested) {
    return { get: () => nested.textContent ?? '', set: (text) => (nested.textContent = text) };
  }

  const added = button.querySelector(':scope > .add-to-cart__added');
  if (added) {
    // Horizon's own render has no whitespace between the icon and the label
    // (`{{-` trims it). A whitespace node there (left by the restored markup)
    // renders as a space, widening the centred content so the icon and text
    // jumped ~2.5px sideways each time the two versions swapped. Drop it.
    [...button.childNodes].forEach((node) => {
      const next = node.nextSibling;
      if (
        node.nodeType === Node.TEXT_NODE &&
        !node.nodeValue?.trim() &&
        next?.nodeType === Node.TEXT_NODE &&
        next.nodeValue?.trim()
      ) {
        node.remove();
      }
    });

    const textNodes = () =>
      [...button.childNodes].filter((node) => node.nodeType === Node.TEXT_NODE && node.nodeValue?.trim());
    return {
      get: () => textNodes().map((node) => node.nodeValue?.trim()).join(' '),
      set: (text) => {
        const [first, ...rest] = textNodes();
        rest.forEach((node) => node.remove());
        if (first) first.nodeValue = text;
        else button.insertBefore(document.createTextNode(text), added);
      },
    };
  }

  if (rebuilt) return null;
  const template = document.getElementById(LABEL_TEMPLATE_ID);
  if (!(template instanceof HTMLTemplateElement)) return null;
  const text = button.textContent?.trim() ?? '';
  button.replaceChildren(template.content.cloneNode(true));
  const label = buttonLabel(button, true);
  label?.set(text);
  return label;
}

/** @param {HTMLFormElement} form */
function sync(form) {
  // Drop WOD's broken selling plan field; a real numeric plan id is left alone.
  form.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
    if (!NUMERIC.test(/** @type {HTMLInputElement} */ (input).value)) input.remove();
  });

  const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[name="add"]'));
  const variantId = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'))?.value;
  if (!button || !variantId) return;

  // Horizon renders the button disabled for unavailable variants: leave those
  // to Horizon / Klaviyo's back-in-stock button.
  if (button.disabled) return;

  // Only repair the icon when WOD has emptied the button; otherwise Horizon's
  // markup is intact and we touch nothing for non pre-order variants.
  const label = buttonLabel(button);
  if (!isPreorderVariant(variantId) || !label) return;

  const preorderText = String(wodSettingFor(variantId)?.a ?? '').trim();
  if (preorderText && label.get() !== preorderText) label.set(preorderText);

  const note = noteField();
  if (!form.querySelector(`input[name="${CSS.escape(note.name)}"]`)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.id = 'preorder-note';
    input.name = note.name;
    input.value = note.value;
    form.append(input);
  }
}

/**
 * Flags pages whose buy form is for a WOD pre-order product, so CSS in
 * theme.liquid can keep the button on its own row on mobile. Otherwise the
 * brief short labels during a variant change ("Add to cart", "Sold out") fit
 * beside the quantity selector and the button jumps back and forth.
 * Set on <html> because Horizon morphs the form and would strip it there.
 */
function flagPreorderProduct() {
  const settings = window.pn?.settings?.singleProductSettings;
  if (!settings) return;
  const productIds = new Set(
    [...document.querySelectorAll(`${FORM_SELECTOR} input[name="product-id"]`)].map(
      (input) => /** @type {HTMLInputElement} */ (input).value
    )
  );
  const isPreorderProduct = Object.values(settings).some(
    (setting) => setting && String(setting.b) === 'true' && productIds.has(String(setting.p))
  );
  document.documentElement.classList.toggle('wod-preorder-product', isPreorderProduct);
}

/**
 * Mixed-cart notice in the cart drawer (snippets/preorder-mixed-cart-notice.liquid).
 * The snippet decides when to render; this fills in the title and text from
 * WOD's "Mixed Cart Warning" settings so the wording stays editable in the app,
 * and hides the notice if that warning is switched off there. Horizon morphs
 * the drawer on every cart change, which resets the notice to the snippet's
 * fallback wording and drops our marker attribute, so it is refilled each time.
 */
function fillMixedCartNotice() {
  const shop = window.pn?.shop;
  if (!shop) return;

  document.querySelectorAll('[data-preorder-mixed-cart-notice]').forEach((notice) => {
    if (!(notice instanceof HTMLElement) || notice.dataset.wodFilled === 'true') return;

    const enabled = String(shop.mixed_cart_warning_enabled) !== 'false';
    notice.hidden = !enabled;

    const title = notice.querySelector('[data-preorder-mixed-cart-title]');
    const text = notice.querySelector('[data-preorder-mixed-cart-text]');
    if (enabled && title && shop.mixed_cart_warning_title) title.innerHTML = shop.mixed_cart_warning_title;
    if (enabled && text && shop.mixed_cart_warning_text) text.innerHTML = shop.mixed_cart_warning_text;

    notice.dataset.wodFilled = 'true';
  });
}

/* "Okay" (or the ×) minimises the notice to one line; "View" reopens it. The
   choice lasts for the browsing session. Horizon re-renders the drawer expanded
   on every cart change, so the state is re-applied from here each time (this
   runs from the MutationObserver, before the browser paints). */
const NOTICE_COLLAPSED_KEY = 'smoco:mixed-cart-notice-collapsed';

function noticeCollapsed() {
  try {
    return sessionStorage.getItem(NOTICE_COLLAPSED_KEY) === 'true';
  } catch (error) {
    return window.__smocoNoticeCollapsed === true;
  }
}

/** @param {boolean} collapsed */
function setNoticeCollapsed(collapsed) {
  try {
    sessionStorage.setItem(NOTICE_COLLAPSED_KEY, String(collapsed));
  } catch (error) {
    window.__smocoNoticeCollapsed = collapsed;
  }
  applyNoticeCollapsed();
}

function applyNoticeCollapsed() {
  const collapsed = noticeCollapsed();
  document.querySelectorAll('[data-preorder-mixed-cart-notice]').forEach((notice) => {
    if (notice.classList.contains('is-collapsed') !== collapsed) notice.classList.toggle('is-collapsed', collapsed);
    notice.querySelectorAll('[aria-expanded]').forEach((button) => {
      const expanded = String(!collapsed);
      if (button.getAttribute('aria-expanded') !== expanded) button.setAttribute('aria-expanded', expanded);
    });
  });
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (target?.closest('[data-preorder-mixed-cart-ok]')) setNoticeCollapsed(true);
  else if (target?.closest('[data-preorder-mixed-cart-view]')) setNoticeCollapsed(false);
});

function syncAll() {
  fillMixedCartNotice();
  applyNoticeCollapsed();
  flagPreorderProduct();
  document.querySelectorAll(FORM_SELECTOR).forEach((form) => sync(/** @type {HTMLFormElement} */ (form)));
}

// Horizon morphs the buttons after fetching the new variant, and WOD writes to
// them on its own timers, so re-sync on any change. This runs straight from the
// observer (a microtask), not on the next animation frame, so the stock label
// is replaced before the browser can paint it. sync() only writes when
// something is wrong, so its own writes settle rather than loop.
new MutationObserver(syncAll).observe(document.body, { childList: true, subtree: true, characterData: true });
syncAll();

/* ------------------------------------------------------------------ *
 * 2. Cart request guard
 * ------------------------------------------------------------------ *
 * A click during a variant change is queued by Horizon and submitted later,
 * after the DOM pass above may have run, so the request itself is the one
 * place that is always right: strip a non-numeric selling_plan, and make sure
 * pre-order variants carry WOD's note.
 */

/** @param {FormData} body */
function guardFormData(body) {
  const plan = body.get('selling_plan');
  if (plan !== null && !NUMERIC.test(String(plan))) body.delete('selling_plan');

  const variantId = body.get('id');
  const note = noteField();
  if (variantId && isPreorderVariant(variantId) && !body.has(note.name)) body.set(note.name, note.value);
}

/**
 * Adds clicked while a variant change is in flight are queued by Horizon and
 * sent later as JSON (`{items: [{id, quantity}]}`) with no line item
 * properties, so WOD's note never reaches them. Fast clickers were getting
 * pre-order items in the cart without the note.
 * @param {string} body
 * @returns {string}
 */
function guardJson(body) {
  const payload = JSON.parse(body);
  const noteKey = window.pn?.shop?.pn_note_label || 'Note';
  const { value } = noteField();
  const lines = Array.isArray(payload.items) ? payload.items : [payload];

  for (const line of lines) {
    if (!line || typeof line !== 'object') continue;
    if ('selling_plan' in line && !NUMERIC.test(String(line.selling_plan))) delete line.selling_plan;
    if (line.id && isPreorderVariant(line.id)) {
      line.properties = { ...line.properties };
      if (!(noteKey in line.properties)) line.properties[noteKey] = value;
    }
  }
  return JSON.stringify(payload);
}

const nativeFetch = window.fetch;
window.fetch = function (input, init) {
  try {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input?.url ?? '';
    const body = init?.body;
    if (url.includes('/cart/add')) {
      if (body instanceof FormData) guardFormData(body);
      else if (typeof body === 'string' && body.trim().startsWith('{')) init = { ...init, body: guardJson(body) };
    }
  } catch (error) {
    // Never block an add to cart because of this guard.
  }
  return nativeFetch.call(this, input, init);
};
